/**
 * Feast resolution.
 *
 * The engine ships a hard-coded list of feasts (mirror of what the database
 * seeders insert). We return slugs only, the API hydrates them against the
 * `feasts` table to obtain names, tropars, descriptions, and so on. This
 * keeps the engine I/O-free.
 *
 * Two kinds of feasts:
 *
 *   - Fixed:    occur every year on the same Gregorian month/day
 *               (e.g. Nativity = Dec 25).
 *   - Movable:  occur a fixed offset of days from Pascha
 *               (e.g. Pentecost = Pascha + 49).
 *
 * `resolveFeasts(date, paschaForYear)` returns every feast slug that lands
 * on `date`, with the highest-rank one first.
 */
import { diffDays, fixedDateFor, sameDay, } from './dates.js';
const RANK_ORDER = { great: 0, major: 1, minor: 2, commemoration: 3 };
export const FIXED_FEASTS = Object.freeze([
    // Twelve Great Feasts (fixed)
    { slug: 'nativity-theotokos', type: 'great', isMovable: false, fixedMonth: 9, fixedDay: 8 },
    { slug: 'exaltation-cross', type: 'great', isMovable: false, fixedMonth: 9, fixedDay: 14 },
    { slug: 'entry-theotokos', type: 'great', isMovable: false, fixedMonth: 11, fixedDay: 21 },
    { slug: 'nativity-christ', type: 'great', isMovable: false, fixedMonth: 12, fixedDay: 25 },
    { slug: 'theophany', type: 'great', isMovable: false, fixedMonth: 1, fixedDay: 6 },
    { slug: 'meeting-lord', type: 'great', isMovable: false, fixedMonth: 2, fixedDay: 2 },
    { slug: 'annunciation', type: 'great', isMovable: false, fixedMonth: 3, fixedDay: 25 },
    { slug: 'transfiguration', type: 'great', isMovable: false, fixedMonth: 8, fixedDay: 6 },
    { slug: 'dormition-theotokos', type: 'great', isMovable: false, fixedMonth: 8, fixedDay: 15 },
    // Major fixed feasts
    { slug: 'circumcision-christ', type: 'major', isMovable: false, fixedMonth: 1, fixedDay: 1 },
    { slug: 'three-holy-hierarchs', type: 'major', isMovable: false, fixedMonth: 1, fixedDay: 30 },
    { slug: 'forty-martyrs-sebaste', type: 'major', isMovable: false, fixedMonth: 3, fixedDay: 9 },
    { slug: 'forerunner-nativity', type: 'major', isMovable: false, fixedMonth: 6, fixedDay: 24 },
    { slug: 'peter-and-paul', type: 'major', isMovable: false, fixedMonth: 6, fixedDay: 29 },
    { slug: 'synaxis-twelve-apostles', type: 'major', isMovable: false, fixedMonth: 6, fixedDay: 30 },
    { slug: 'prophet-elijah', type: 'major', isMovable: false, fixedMonth: 7, fixedDay: 20 },
    { slug: 'beheading-forerunner', type: 'major', isMovable: false, fixedMonth: 8, fixedDay: 29 },
    { slug: 'protection-theotokos', type: 'major', isMovable: false, fixedMonth: 10, fixedDay: 1 },
    { slug: 'archangels', type: 'major', isMovable: false, fixedMonth: 11, fixedDay: 8 },
    { slug: 'st-nicholas', type: 'major', isMovable: false, fixedMonth: 12, fixedDay: 6 },
    { slug: 'conception-theotokos', type: 'major', isMovable: false, fixedMonth: 12, fixedDay: 9 },
    { slug: 'st-stephen-protomartyr', type: 'major', isMovable: false, fixedMonth: 12, fixedDay: 27 },
]);
export const MOVABLE_FEASTS = Object.freeze([
    // The Feast of Feasts itself
    { slug: 'pascha', type: 'great', isMovable: true, daysOffsetFromPascha: 0 },
    // Twelve Great Feasts (movable)
    { slug: 'palm-sunday', type: 'great', isMovable: true, daysOffsetFromPascha: -7 },
    { slug: 'ascension', type: 'great', isMovable: true, daysOffsetFromPascha: 39 },
    { slug: 'pentecost', type: 'great', isMovable: true, daysOffsetFromPascha: 49 },
    // Pre-Lent + Lent (Triodion) Sundays
    {
        slug: 'sunday-of-publican-pharisee',
        type: 'minor',
        isMovable: true,
        daysOffsetFromPascha: -70,
    },
    { slug: 'sunday-of-prodigal-son', type: 'minor', isMovable: true, daysOffsetFromPascha: -63 },
    { slug: 'meatfare-sunday', type: 'minor', isMovable: true, daysOffsetFromPascha: -56 },
    { slug: 'cheesefare-sunday', type: 'minor', isMovable: true, daysOffsetFromPascha: -49 },
    { slug: 'sunday-of-orthodoxy', type: 'major', isMovable: true, daysOffsetFromPascha: -42 },
    {
        slug: 'sunday-of-st-gregory-palamas',
        type: 'minor',
        isMovable: true,
        daysOffsetFromPascha: -35,
    },
    { slug: 'sunday-of-cross', type: 'minor', isMovable: true, daysOffsetFromPascha: -28 },
    { slug: 'sunday-of-st-john-climacus', type: 'minor', isMovable: true, daysOffsetFromPascha: -21 },
    { slug: 'sunday-of-st-mary-of-egypt', type: 'minor', isMovable: true, daysOffsetFromPascha: -14 },
    // Holy Week
    { slug: 'lazarus-saturday', type: 'major', isMovable: true, daysOffsetFromPascha: -8 },
    { slug: 'great-and-holy-monday', type: 'major', isMovable: true, daysOffsetFromPascha: -6 },
    { slug: 'great-and-holy-tuesday', type: 'major', isMovable: true, daysOffsetFromPascha: -5 },
    { slug: 'great-and-holy-wednesday', type: 'major', isMovable: true, daysOffsetFromPascha: -4 },
    { slug: 'great-and-holy-thursday', type: 'major', isMovable: true, daysOffsetFromPascha: -3 },
    { slug: 'great-and-holy-friday', type: 'major', isMovable: true, daysOffsetFromPascha: -2 },
    { slug: 'great-and-holy-saturday', type: 'major', isMovable: true, daysOffsetFromPascha: -1 },
    // Pentecostarion
    { slug: 'bright-monday', type: 'minor', isMovable: true, daysOffsetFromPascha: 1 },
    { slug: 'thomas-sunday', type: 'major', isMovable: true, daysOffsetFromPascha: 7 },
    { slug: 'sunday-of-myrrhbearers', type: 'minor', isMovable: true, daysOffsetFromPascha: 14 },
    { slug: 'mid-pentecost', type: 'minor', isMovable: true, daysOffsetFromPascha: 24 },
    { slug: 'sunday-of-blind-man', type: 'minor', isMovable: true, daysOffsetFromPascha: 35 },
    { slug: 'all-saints-sunday', type: 'major', isMovable: true, daysOffsetFromPascha: 56 },
]);
/**
 * Returns every feast that lands on `date`, ordered by rank
 * (great → major → minor → commemoration).
 */
export function resolveFeasts(date, paschaForYear, calendarStyle = 'new') {
    const matches = [];
    // Fixed feasts are matched against the menaion date for the user's calendar
    // (shifted for Old Calendar); movable feasts stay on the civil date below.
    const fixed = fixedDateFor(date, calendarStyle);
    for (const f of FIXED_FEASTS) {
        if (f.fixedMonth === fixed.month && f.fixedDay === fixed.day) {
            matches.push({ slug: f.slug, type: f.type, isMovable: false });
        }
    }
    const offset = diffDays(paschaForYear, date);
    for (const f of MOVABLE_FEASTS) {
        if (f.daysOffsetFromPascha === offset) {
            matches.push({ slug: f.slug, type: f.type, isMovable: true });
        }
    }
    matches.sort((a, b) => RANK_ORDER[a.type] - RANK_ORDER[b.type]);
    return matches;
}
/**
 * Convenience: the highest-rank feast on this date, or null. Ties are broken
 * in declaration order (which puts the Pascha-anchored ones after the fixed
 * ones - that matches typikon precedence on most clashes).
 */
export function primaryFeast(date, paschaForYear, calendarStyle = 'new') {
    const list = resolveFeasts(date, paschaForYear, calendarStyle);
    return list[0] ?? null;
}
/** Quick check used by fasting rules. */
export function isPaschaWeek(date, paschaForYear) {
    const offset = diffDays(paschaForYear, date);
    return offset >= 0 && offset <= 6;
}
export function isHolyWeek(date, paschaForYear) {
    const offset = diffDays(paschaForYear, date);
    return offset >= -7 && offset <= -1;
}
export function isTransfiguration(date) {
    return date.month === 8 && date.day === 6;
}
export function isAnnunciation(date) {
    return date.month === 3 && date.day === 25;
}
export function isPalmSunday(date, paschaForYear) {
    return (sameDay(date, { ...paschaForYear, day: paschaForYear.day - 7 }) ||
        diffDays(paschaForYear, date) === -7);
}
//# sourceMappingURL=feasts.js.map